<template>
  <div
    class="order-item"
    :class="{
      'order-item--hidden': itemIsHidden,
      'order-item--removed': itemIsRemoved,
      'order-item--out-of-stock': outOfStock,
      'order-item--checkout': readOnly,
    }"
  >
    <a :href="productPath">
      <img
        :src="item.imageUrl"
        class="order-item__image"
        :class="`order-item__image--${sizeModifier}`"
        :alt="item.groupName + ' - ' + item.color"
      />
    </a>
    <div class="order-item__product-info">
      <div class="order-item__description">
        <a
          :href="productPath"
          class="order-item__title"
          :class="`order-item__title--${sizeModifier}`"
          >{{ item.groupName }}</a
        >
        <div v-if="!isGiftCardOrder" class="order-item__color">{{ item.color }}</div>
        <div class="order-item__size">{{ item.size }}</div>
        <div
          v-if="isGiftCardOrder"
          ref="giftCardMessage"
          class="order-item__gift-card-msg"
          :class="{ 'order-item__gift-card-msg--truncated': !showFullGiftCardMsg }"
        >
          {{ item.giftCardMessage }}
        </div>
        <div v-else class="order-item__quantity" :class="`order-item__quantity--${sizeModifier}`">
          <span class="order-item__quantity-text">
            <span
              v-if="!readOnly"
              class="order-item__quantity-decrease"
              @click="changeQuantity(-1)"
            >
              <span class="order-item__minus icon-minus"></span>
            </span>
            <span v-if="readOnly" class="order-item__quantity-label">{{ $t('quantity') }}:</span>
            <span class="order-item__quantity-number">{{ this.quantity }}</span>
            <span
              v-if="!readOnly"
              class="order-item__quantity-increase"
              :class="{ 'order-item__quantity-increase--max': maximumQuantityReached }"
              @click="changeQuantity(1)"
            >
              <span class="order-item__plus icon-plus"></span>
            </span>
          </span>
        </div>
      </div>
      <div v-if="!readOnly" class="order-item__remove icon-trash" @click="removeItem"></div>
      <div class="order-item__price">{{ itemTotal }}</div>
      <span class="order-item__flex-break-row"></span>
      <div
        v-if="isLowStock"
        class="order-item__low-stock"
        :class="{ 'order-item__low-stock--checkout': readOnly }"
      >
        {{ $tPlural('pdp.purchase_pod.low_stock', item.stock) }}
      </div>
      <div
        v-if="outOfStock"
        class="order-item__out-of-stock"
        :class="`order-item__out-of-stock--${sizeModifier}`"
      >
        <p class="order-item__out-of-stock-msg">
          <i class="icon icon--red icon-alert"></i>{{ $t('out_of_stock') }}
        </p>
        <p
          v-if="item.isBackorderable"
          class="order-item__out-of-stock-notification"
          @click="showOutOfStockNotification(item)"
        >
          {{ $t('out_of_stock_notification') }}<i class="icon-carousel-arrow"></i>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { getSlugFromGroupName } from '@/helpers/url.js'
import { format } from '@/helpers/currency-formatter'
import { isLowStock } from '@/stores/product'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'

export const SIZE_MODIFIERS = {
  default: 'default',
  large: 'large',
}

export default {
  name: 'OrderItem',
  props: {
    id: { type: Number, required: true },
    item: { type: Object, required: true },
    index: { type: Number, required: true },
    quantity: { type: Number, default: 1, required: true }, // Adding 1 as default to make sure it doesn't break items where quantity is not defined
    stock: { type: Number, default: 0 },
    ignoreStock: { type: Boolean, default: false },
    onCartContentChange: { type: Function, default: () => {} },
    readOnly: { type: Boolean, default: false },
    sizeModifier: {
      type: String,
      default: SIZE_MODIFIERS.default,
      validator(variant) {
        return Object.values(SIZE_MODIFIERS).includes(variant)
      },
    },
    flashContext: { type: String },
    isGiftCardOrder: { type: Boolean, default: false },
    showFullGiftCardMsg: { type: Boolean, default: false },
  },

  data() {
    return {
      itemHeight: 0,
      itemIsHidden: false,
      itemIsRemoved: false,
      trans: this.$t('errors.invalid_email'),
    }
  },

  computed: {
    ...mapState('store', ['currency']),

    ...mapGetters({
      viewportWidth: 'viewport/width',
    }),

    itemTotal() {
      const amount = this.item.price * this.quantity
      return format(amount, this.currency)
    },

    maximumQuantityReached() {
      return this.quantity === this.stock || this.outOfStock
    },

    outOfStock() {
      return (this.quantity > this.stock || !this.stock) && !this.ignoreStock
    },

    productPath() {
      return this.outOfStock ? 'javascript:;' : this.item.productPath
    },

    isLowStock() {
      return isLowStock(this.stock) && !this.ignoreStock
    },
  },

  watch: {
    outOfStock: {
      immediate: true,
      handler(value) {
        if (value) this.preloadOutOfStockOverlay()
      },
    },
  },

  mounted() {
    this.itemHeight = this.$el.offsetHeight
  },

  methods: {
    ...mapActions({
      showOutOfStockOverlay: 'overlay/showOutOfStockOverlay',
      preloadOutOfStockOverlay: 'overlay/preloadOutOfStockOverlay',
    }),

    removeItem() {
      this.$el.style.height = this.itemHeight + 'px'
      // First animate disappearance of item
      this.$nextTick(() => (this.itemIsHidden = true))

      setTimeout(() => {
        // Needed for animation purposes
        this.itemIsHidden = false
        this.itemIsRemoved = true
        this.$nextTick(() => (this.itemIsRemoved = false))
        this.onCartContentChange(this.index)
      }, On.TransitionTimes.slow)

      let payload = { skuId: this.id, quantity: 0, increase: this.quantity * -1 }
      this.$store.dispatch('order/updateCartItemNumber', payload)
      this.trackOrderChange(payload, true)
    },

    changeQuantity(increase) {
      if (this.maximumQuantityReached && increase > 0) return
      // TODO: Add flash message to navigation?
      // return window.On.Store.dispatch('flash/addMessage', {
      //   text: this.$t('minicart.maximum_stock'),
      //   type: MESSAGE_TYPES.warning,
      // })

      const newQuantity = this.quantity + increase
      const onError = message => {
        this.$store.dispatch('flash/addMessage', {
          text: message,
          type: 'error',
          context: this.flashContext,
        })
      }
      let payload = { skuId: this.id, quantity: newQuantity, increase, onError }
      newQuantity <= 0 ? this.removeItem(payload) : this.updateQuantity(payload)
    },

    updateQuantity(payload) {
      this.$store.dispatch('order/updateCartItemNumber', payload)
      this.trackOrderChange(payload)
    },

    trackOrderChange(payload, isCompleteRemoval = false) {
      const action = payload.increase > 0 ? ACTIONS.addToCart : ACTIONS.removeFromCart
      const quantity = isCompleteRemoval ? this.quantity : 1

      notifyExternalServices({
        action,
        product: {
          ...this.item,
          quantity,
          productSku: this.item.sku,
          sku: this.item.variantSku,
          currency: this.currency.code,
        },
      })
    },

    showOutOfStockNotification(item) {
      const { variantSku: sku, isBackorderable, imageUrl, groupName } = item
      this.showOutOfStockOverlay({
        sku,
        slug: getSlugFromGroupName(groupName),
        imageUrl,
        isBackorderable,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './OrderItem';
</style>

<i18n src="@/locales/order_item.json">
</i18n>
